import { isFileUploadAuthError, isFileUploadDeferralError, uploadFileToS3 } from './file-upload-service';

let lastDeferredDocumentUploadLogAt = 0;
let lastAuthSkippedDocumentUploadLogAt = 0;
const DEFERRED_DOCUMENT_UPLOAD_LOG_INTERVAL_MS = 15000;
const AUTH_SKIPPED_DOCUMENT_UPLOAD_LOG_INTERVAL_MS = 15000;

function isPdfDocument(doc) {
    const name = String(doc?.name || doc?.originalName || '').toLowerCase();
    const ext = String(doc?.ext || name.split('.').pop() || '').toLowerCase();
    const mime = String(doc?.contentType || doc?.mimeType || '').toLowerCase();
    return ext === 'pdf' || name.endsWith('.pdf') || mime === 'application/pdf';
}

function isPortableDataPdf(src) {
    return typeof src === 'string'
        && src.startsWith('data:application/pdf')
        && src.includes(';base64,')
        && src.length > 200;
}

function isBlobUrl(src) {
    return typeof src === 'string' && src.startsWith('blob:');
}

function portablePdfDataUrlToBlob(src) {
    const commaIndex = String(src).indexOf(',');
    if (commaIndex < 0) return null;
    const binary = atob(String(src).slice(commaIndex + 1));
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    return new Blob([bytes], { type: 'application/pdf' });
}

function getTransientPdfBlob(doc) {
    const candidate = doc?.pdfBlob || doc?.pdfFile;
    return typeof Blob !== 'undefined' && candidate instanceof Blob ? candidate : null;
}

function documentHasCloudBacking(doc) {
    return !!(
        doc?.fileMetadataId
        || doc?.pdfS3Key
        || doc?.s3Key
        || doc?.fileS3Key
        || doc?.cloudObjectKey
    );
}

function documentHasLocalUploadSource(doc) {
    if (!doc || typeof doc !== 'object') return false;
    if (doc.docBlobId) return true;
    if (doc.fileHandle?.getFile || doc.handle?.getFile) return true;
    if ([
        doc.file,
        doc.blob,
        doc.pdfBlob,
        doc.pdfFile,
        doc.bytes,
        doc.docBytes,
        doc.arrayBuffer,
    ].some(value => value != null)) return true;
    return [doc.data, doc.pdfDataUrl].some(value => (
        typeof value === 'string'
        && (value.startsWith('data:') || value.startsWith('blob:'))
    ));
}

function stripTransientPdfFields(doc) {
    if (!doc || typeof doc !== 'object') return doc;
    if (!Object.prototype.hasOwnProperty.call(doc, 'pdfBlob')
        && !Object.prototype.hasOwnProperty.call(doc, 'pdfFile')
    ) {
        return doc;
    }
    const { pdfBlob: _pdfBlob, pdfFile: _pdfFile, ...rest } = doc;
    return rest;
}

export function documentNeedsBackendUpload(doc) {
    return !!(
        doc
        && !documentHasCloudBacking(doc)
        && documentHasLocalUploadSource(doc)
    );
}

async function pdfPreviewSourceToBlob(src) {
    const response = await fetch(src);
    if (!response.ok) throw new Error(`PDF source fetch failed (${response.status})`);
    const blob = await response.blob();
    if (!blob || blob.size <= 0) throw new Error('PDF source was empty');
    return blob.type === 'application/pdf'
        ? blob
        : blob.slice(0, blob.size, 'application/pdf');
}

function stripNonPortablePdfUrl(doc, reason = 'stale_blob_url') {
    if (!isPdfDocument(doc) || !isBlobUrl(doc?.pdfDataUrl)) return doc;
    return {
        ...stripTransientPdfFields(doc),
        pdfDataUrl: undefined,
        pdfPreviewUnavailableReason: doc.pdfPreviewUnavailableReason || reason,
    };
}

function queuedDocumentUploadError(queueEntry, cause = undefined) {
    return Object.assign(
        new Error('PDF upload is owned by the persistent background queue.', cause ? { cause } : undefined),
        {
            code: 'DOCUMENT_DURABILITY_REQUIRED',
            retryable: true,
            retryOwner: 'upload-queue',
            queueId: queueEntry?.id ?? null,
        },
    );
}

function logAuthSkippedDocumentUploadOnce() {
    const now = Date.now();
    if (now - lastAuthSkippedDocumentUploadLogAt < AUTH_SKIPPED_DOCUMENT_UPLOAD_LOG_INTERVAL_MS) return;
    lastAuthSkippedDocumentUploadLogAt = now;
    console.info('[QM] PDF cloud persistence skipped because the current session is not authenticated; project save will continue with local document references.');
}

export function normalizeDocumentsForProjectLoad(documents = []) {
    if (!Array.isArray(documents) || documents.length === 0) return [];
    let changed = false;
    const normalized = documents.map((doc) => {
        if (!doc || typeof doc !== 'object') return doc;
        const strippedTransient = stripTransientPdfFields(doc);
        if (strippedTransient !== doc) changed = true;
        if (isPdfDocument(strippedTransient)
            && isBlobUrl(strippedTransient.pdfDataUrl)
        ) {
            changed = true;
            if (strippedTransient.fileMetadataId || strippedTransient.pdfS3Key) {
                return { ...strippedTransient, pdfDataUrl: undefined };
            }
            return stripNonPortablePdfUrl(strippedTransient, 'history_blob_url');
        }
        return strippedTransient;
    });

    // De-dupe documents that share an identical `id` (2026-06-27). A historical
    // documents-array race (see merge-docs-by-identity.js, the 2026-06-25
    // "drops the files" race) could persist the SAME generated doc id twice into
    // project.documents. Two entries with the same id then render with the same
    // React `key` in Stage 1 (Stage1DataEntry.jsx documents.map -> key={doc.id ||
    // i}) and in Stages 2/4, producing the "Encountered two children with the
    // same key" warning every render AND a visibly duplicated row. The id is
    // `doc_<Date.now()>_<rand>`, so a collision between two genuinely-different
    // documents is effectively impossible: an identical id is always the same
    // document. Drop later copies, keep the FIRST occurrence (preserves order +
    // data). Documents with no id are never de-duped (they render with
    // key=index, which is already unique per position).
    const seenIds = new Set();
    const deduped = [];
    for (const doc of normalized) {
        const id = doc && typeof doc === 'object' ? doc.id : undefined;
        if (id != null && id !== '') {
            if (seenIds.has(id)) { changed = true; continue; }
            seenIds.add(id);
        }
        deduped.push(doc);
    }

    return changed ? deduped : documents;
}

/**
 * Make document records safe to persist and reopen after a browser refresh.
 * Blob URLs are session-local, so PDF blobs are uploaded to the backend file
 * store when possible. If an old blob URL is already stale, strip it so the UI
 * shows extracted text instead of a blank iframe.
 */
export async function makeDocumentArrayPortableForPersistence(documents = [], projectId) {
    if (!Array.isArray(documents) || documents.length === 0) return [];

    const portable = [];
    let changed = false;
    for (let i = 0; i < documents.length; i++) {
        const doc = documents[i];
        if (!doc || typeof doc !== 'object') {
            portable.push(doc);
            continue;
        }
        const docWithoutTransient = stripTransientPdfFields(doc);

        if (doc.fileMetadataId || doc.pdfS3Key) {
            if (isPdfDocument(doc) && doc.pdfDataUrl !== undefined) {
                changed = true;
                portable.push({ ...docWithoutTransient, pdfDataUrl: undefined });
            } else {
                portable.push(docWithoutTransient);
            }
            continue;
        }

        // This fast path uploads PDFs directly for immediate preview. Other
        // document formats still need the first-save parent shell, but their
        // bytes are migrated to docBlobId below the hook and promoted by the
        // shared DataServiceAPI durability boundary.
        if (!isPdfDocument(doc) || !documentNeedsBackendUpload(doc)) {
            const stripped = stripNonPortablePdfUrl(docWithoutTransient);
            if (stripped !== doc) changed = true;
            portable.push(stripped);
            continue;
        }

        // Resolve the exact PDF bytes before entering the shared queue claim.
        // The claim transaction is the one ownership boundary used by both
        // foreground saves and background workers, closing the old
        // queue-lookup -> direct-upload race across tabs.
        let uploadData = null;
        let queueBlob = null;
        let queueClaim = null;
        let uploadQueue = null;
        const entityId = doc.id || `doc_${i}`;
        try {
            uploadData = getTransientPdfBlob(doc)
                || (isPortableDataPdf(doc.pdfDataUrl)
                    ? doc.pdfDataUrl
                    : await pdfPreviewSourceToBlob(doc.pdfDataUrl));
            queueBlob = uploadData instanceof Blob
                ? uploadData
                : (isPortableDataPdf(uploadData) ? portablePdfDataUrlToBlob(uploadData) : null);

            if (queueBlob instanceof Blob) {
                try {
                    uploadQueue = await import('./upload-queue');
                    const ownership = await uploadQueue.acquireUploadQueueClaim({
                        projectId,
                        entityType: 'project_document',
                        entityId,
                        blob: queueBlob,
                        contentType: 'application/pdf',
                        originalFilename: doc.name || doc.originalName || `document-${i + 1}.pdf`,
                    });
                    if (ownership?.state === 'confirmed' && ownership.item?.s3Key) {
                        portable.push({
                            ...docWithoutTransient,
                            pdfS3Key: ownership.item.s3Key,
                            fileMetadataId: ownership.item.fileMetadataId || undefined,
                            contentType: ownership.item.contentType || doc.contentType || 'application/pdf',
                            pdfDataUrl: undefined,
                            pdfPreviewUnavailableReason: undefined,
                        });
                        changed = true;
                        continue;
                    }
                    if (ownership?.state === 'waiting') {
                        throw queuedDocumentUploadError(ownership.item);
                    }
                    if (ownership?.state === 'claimed') queueClaim = ownership.item;
                } catch (queueClaimErr) {
                    if (queueClaimErr?.retryOwner === 'upload-queue') throw queueClaimErr;
                    throw Object.assign(
                        new Error('Could not establish one durable PDF upload owner.', { cause: queueClaimErr }),
                        {
                            code: 'DOCUMENT_DURABILITY_REQUIRED',
                            retryable: true,
                            retryOwner: 'upload-queue-claim',
                        },
                    );
                }
            }

            const result = await uploadFileToS3({
                data: uploadData,
                projectId,
                entityType: 'project_document',
                entityId,
                contentType: 'application/pdf',
                originalFilename: doc.name || doc.originalName || `document-${i + 1}.pdf`,
            });
            if (queueClaim) {
                const confirmed = await uploadQueue.confirmUploadQueueClaim(queueClaim, result);
                if (!confirmed) {
                    throw queuedDocumentUploadError(queueClaim, new Error('PDF upload claim changed before confirmation.'));
                }
            }
            portable.push({
                ...docWithoutTransient,
                pdfS3Key: result.s3Key,
                fileMetadataId: result.fileMetadataId || undefined,
                contentType: result.contentType || doc.contentType || 'application/pdf',
                pdfDataUrl: undefined,
                pdfPreviewUnavailableReason: undefined,
            });
            changed = true;
        } catch (err) {
            if (err?.code === 'DOCUMENT_DURABILITY_REQUIRED' && err?.retryOwner) throw err;
            // CZ-A (PRICING-TRACE 2026-05-15): distinguish the failure causes
            // so the UI can render the right recovery affordance per case.
            // The architect's investigation found that the old single
            // `'upload_failed_or_stale_blob'` reason hid the difference
            // between "user wasn't signed in" (recoverable by re-signing in)
            // and "backend was rate limited" (recoverable by retrying later).
            let failureReason = 'upload_failed';
            let shouldQueueRetry = false;
            let queueRetryAfterMs = null;
            if (isFileUploadDeferralError(err)) {
                failureReason = 'rate_limited';
                shouldQueueRetry = true;
                queueRetryAfterMs = Math.max(
                    1000,
                    err?.retryAfterMs || (err?.retryAfterSeconds || 1) * 1000,
                );
                const now = Date.now();
                if (now - lastDeferredDocumentUploadLogAt >= DEFERRED_DOCUMENT_UPLOAD_LOG_INTERVAL_MS) {
                    lastDeferredDocumentUploadLogAt = now;
                    const seconds = err?.retryAfterSeconds || Math.ceil((err?.retryAfterMs || 0) / 1000) || 1;
                    console.info(`[QM] PDF persistence deferred for ${seconds}s after backend rate limiting; the persistent queue owns the retry.`);
                }
            } else if (isFileUploadAuthError(err)) {
                failureReason = 'auth_skipped';
                logAuthSkippedDocumentUploadOnce();
            } else {
                shouldQueueRetry = true;
                console.warn(`[QM] PDF persistence failed for ${doc.name || doc.originalName || `document-${i + 1}`}:`, err?.message);
            }
            // Return this SAME claimed row to the background queue. No second
            // enqueue/read boundary exists, so another tab cannot become a
            // concurrent foreground uploader during the hand-off.
            if (shouldQueueRetry && queueClaim) {
                try {
                    await uploadQueue.deferUploadQueueClaim(queueClaim, {
                        error: err,
                        retryAfterMs: queueRetryAfterMs || 0,
                    });
                } catch (queueErr) {
                    console.warn('[QM] Could not return the claimed PDF to its background retry owner:', queueErr?.message);
                }
                throw queuedDocumentUploadError(queueClaim, err);
            }
            if (failureReason === 'auth_skipped' && queueClaim) {
                try {
                    const released = await uploadQueue.releaseUploadQueueClaim(queueClaim);
                    if (!released) throw new Error('PDF upload claim changed before release.');
                } catch (queueErr) {
                    console.warn('[QM] Could not release the unauthenticated PDF upload claim:', queueErr?.message);
                    throw queuedDocumentUploadError(queueClaim, err);
                }
            }
            // Preserve the complete live source until a durable upload receipt
            // exists. The following local-blob migration and the direct API save
            // boundary can now retry/materialise these exact bytes. Stripping a
            // Blob, File or current-session blob URL here made a failed upload
            // indistinguishable from a successful portable save and could leave
            // the cloud project with metadata/text only.
            changed = true;
            portable.push({
                ...doc,
                pdfPreviewUnavailableReason: doc.pdfPreviewUnavailableReason || failureReason,
            });
        }
    }

    return changed ? portable : documents;
}

/**
 * Retry uploading a single document that previously failed.
 *
 * CZ-A (PRICING-TRACE 2026-05-15): when a PDF was stripped during save with
 * `pdfPreviewUnavailableReason: 'auth_skipped'` (the user wasn't signed in
 * at save time), the user can now sign in and call this from the UI to
 * recover the document. Returns `{ ok, doc, reason }` so the caller can
 * surface the result and update the local document array in place.
 *
 * Caveat: this only retries when a recoverable source is still in memory
 * (transient `pdfBlob` / `pdfFile`, or a still-valid `blob:` `pdfDataUrl`).
 * If those are gone (page reload after the auth-skipped strip), the strip
 * has already discarded the bytes and recovery requires re-import from
 * the user's filesystem. That deeper recovery is M-B's sync-folder path.
 */
export async function retryDeferredDocumentUpload(doc, projectId, docIndex = 0) {
    if (!doc || typeof doc !== 'object') {
        return { ok: false, doc, reason: 'no_doc' };
    }
    if (doc.pdfS3Key || doc.fileMetadataId) {
        return { ok: true, doc, reason: 'already_uploaded' };
    }
    const transientBlob = getTransientPdfBlob(doc);
    const hasUsableSource = !!(transientBlob || isPortableDataPdf(doc.pdfDataUrl) || isBlobUrl(doc.pdfDataUrl));
    if (!hasUsableSource) {
        return { ok: false, doc, reason: 'source_lost' };
    }
    try {
        const uploadData = transientBlob
            || (isPortableDataPdf(doc.pdfDataUrl)
                ? doc.pdfDataUrl
                : await pdfPreviewSourceToBlob(doc.pdfDataUrl));
        const result = await uploadFileToS3({
            data: uploadData,
            projectId,
            entityType: 'project_document',
            entityId: doc.id || `doc_${docIndex}`,
            contentType: 'application/pdf',
            originalFilename: doc.name || doc.originalName || `document-${docIndex + 1}.pdf`,
        });
        return {
            ok: true,
            doc: {
                ...stripTransientPdfFields(doc),
                pdfS3Key: result.s3Key,
                fileMetadataId: result.fileMetadataId || undefined,
                contentType: result.contentType || doc.contentType || 'application/pdf',
                pdfDataUrl: undefined,
                pdfPreviewUnavailableReason: undefined,
            },
            reason: 'uploaded',
        };
    } catch (err) {
        let failureReason = 'upload_failed';
        if (isFileUploadDeferralError(err)) failureReason = 'rate_limited';
        else if (isFileUploadAuthError(err)) failureReason = 'auth_skipped';
        return {
            ok: false,
            doc: { ...doc, pdfPreviewUnavailableReason: failureReason },
            reason: failureReason,
            error: err?.message,
        };
    }
}
